import { useCallback, useEffect, useRef, useState } from "react";

export const MAX_VOICE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const WAVEFORM_BARS = 64;
const SAMPLE_INTERVAL_MS = 80;

export type VoiceRecorderState = "idle" | "recording" | "processing";

export type VoiceRecorderResult = {
  blob: Blob;
  mime: string;
  durationMs: number;
  waveform: number[]; // WAVEFORM_BARS values, 0–1 normalized
};

function pickMimeType(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/ogg;codecs=opus",
    "audio/webm",
    "audio/ogg",
    "audio/mp4",
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "";
}

export type UseVoiceRecorderReturn = {
  state: VoiceRecorderState;
  durationMs: number;
  liveWaveform: number[]; // current amplitude bar (1 value) for live indicator
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
};

export function useVoiceRecorder(
  onReady: (result: VoiceRecorderResult) => void,
  onError: (msg: string) => void,
): UseVoiceRecorderReturn {
  const [state, setState] = useState<VoiceRecorderState>("idle");
  const [durationMs, setDurationMs] = useState(0);
  const [liveWaveform, setLiveWaveform] = useState<number[]>([]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const sampleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const waveformSamplesRef = useRef<number[]>([]);

  const cleanup = useCallback(() => {
    if (sampleTimerRef.current) clearInterval(sampleTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    sampleTimerRef.current = null;
    maxTimerRef.current = null;

    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
      analyserRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    waveformSamplesRef.current = [];
    setDurationMs(0);
    setLiveWaveform([]);
  }, []);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      cleanup();
    };
  }, [cleanup]);

  const start = useCallback(async () => {
    if (state !== "idle") return;
    cancelledRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (err) {
      const msg =
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Доступ к микрофону запрещён — проверьте разрешения браузера"
          : "Не удалось получить доступ к микрофону";
      onError(msg);
      return;
    }
    if (cancelledRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    waveformSamplesRef.current = [];

    // Web Audio API для захвата амплитуды
    try {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx; // сохраняем сразу — чтобы cleanup() закрыл даже при ошибке ниже
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      analyserRef.current = analyser;
    } catch {
      // Если Web Audio API не поддерживается — запись без waveform
    }

    const mime = pickMimeType();
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mr.onstop = () => {
      if (cancelledRef.current) {
        cleanup();
        setState("idle");
        return;
      }
      const durationMsNow = Date.now() - startTimeRef.current;
      // Слишком короткая запись или пустые данные — отменяем молча
      if (durationMsNow < 500 || chunksRef.current.length === 0) {
        cleanup();
        setState("idle");
        return;
      }
      const rawBlob = new Blob(chunksRef.current, { type: mr.mimeType || mime || "audio/webm" });
      const samples = waveformSamplesRef.current;

      // Downsample to WAVEFORM_BARS bars
      const waveform = buildWaveform(samples, WAVEFORM_BARS);

      const result: VoiceRecorderResult = {
        blob: rawBlob,
        mime: rawBlob.type,
        durationMs: durationMsNow,
        waveform,
      };
      cleanup();
      setState("idle");
      onReady(result);
    };

    startTimeRef.current = Date.now();
    mr.start(200); // timeslice 200ms
    setState("recording");

    // Таймер длительности + сбор амплитуды
    sampleTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setDurationMs(elapsed);

      const analyser = analyserRef.current;
      if (analyser) {
        const buf = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(buf);
        // RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length) / 255;
        waveformSamplesRef.current.push(rms);
        setLiveWaveform([rms]);
      }
    }, SAMPLE_INTERVAL_MS);

    // Авто-стоп по лимиту
    maxTimerRef.current = setTimeout(() => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
        setState("processing");
      }
    }, MAX_VOICE_DURATION_MS);
  }, [state, cleanup, onReady, onError]);

  const stop = useCallback(() => {
    if (state !== "recording") return;
    if (sampleTimerRef.current) clearInterval(sampleTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    sampleTimerRef.current = null;
    maxTimerRef.current = null;
    setState("processing");
    mediaRecorderRef.current?.stop();
  }, [state]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (sampleTimerRef.current) clearInterval(sampleTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    sampleTimerRef.current = null;
    maxTimerRef.current = null;
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    } else {
      cleanup();
      setState("idle");
    }
  }, [cleanup]);

  return { state, durationMs, liveWaveform, start, stop, cancel };
}

/** Downsample raw amplitude samples to a fixed number of bars (0–1). */
function buildWaveform(samples: number[], bars: number): number[] {
  if (samples.length === 0) return Array(bars).fill(0.1);
  const result: number[] = [];
  const step = samples.length / bars;
  for (let i = 0; i < bars; i++) {
    const from = Math.floor(i * step);
    const to = Math.min(Math.ceil((i + 1) * step), samples.length);
    let max = 0;
    for (let j = from; j < to; j++) {
      if ((samples[j] ?? 0) > max) max = samples[j] ?? 0;
    }
    result.push(max);
  }
  // Normalize so peak = 1
  const peak = Math.max(...result, 0.01);
  return result.map((v) => v / peak);
}
